import type { RunState, ScoreBreakdown, StageDefinition } from './types';

export const CHAIN_WINDOW_SECONDS = 2.35;
export const MAX_PULSES = 2;

export interface StageScoreInput {
  petalScore: number;
  maxCombo: number;
  elapsed: number;
  parTime: number;
  pulsesRemaining: number;
  retries: number;
  landingSpeed: number;
  maxLandingSpeed: number;
}

export function petalAward(combo: number): number {
  return 600 + Math.max(0, combo - 1) * 240;
}

export function nextCombo(current: number, sinceLastPetal: number): number {
  return sinceLastPetal <= CHAIN_WINDOW_SECONDS ? Math.min(8, current + 1) : 1;
}

export function calculateStageScore(input: StageScoreInput): ScoreBreakdown {
  const timeRatio = Math.max(0, Math.min(1, (input.parTime * 1.45 - input.elapsed) / input.parTime));
  const landingRatio = Math.max(0, Math.min(1, 1 - input.landingSpeed / input.maxLandingSpeed));
  const petals = Math.max(0, Math.round(input.petalScore));
  const chain = Math.max(0, input.maxCombo - 1) * 320;
  const time = Math.round(timeRatio * 1_500);
  const pulses = Math.max(0, Math.min(MAX_PULSES, input.pulsesRemaining)) * 650;
  const landing = 900 + Math.round(landingRatio * 1_500);
  const retryPenalty = Math.min(2_400, Math.max(0, input.retries) * 400);
  return {
    petals,
    chain,
    time,
    pulses,
    landing,
    retryPenalty,
    total: Math.max(0, petals + chain + time + pulses + landing - retryPenalty),
  };
}

export function campaignRank(score: number, stages = 5): 'S' | 'A' | 'B' | 'C' {
  const normalized = score / stages;
  if (normalized >= 7_800) return 'S';
  if (normalized >= 6_100) return 'A';
  if (normalized >= 4_400) return 'B';
  return 'C';
}

export function createInitialRun(phase: RunState['phase'] = 'loading'): RunState {
  return {
    phase,
    stageIndex: 0,
    score: 0,
    stageScore: 0,
    combo: 0,
    maxCombo: 0,
    pulses: MAX_PULSES,
    retries: 0,
    elapsed: 0,
    petalsCollected: 0,
    lastPetalAt: Number.NEGATIVE_INFINITY,
    failure: null,
    results: [],
  };
}

export function resetAttempt(run: RunState, countRetry = true): RunState {
  return {
    ...run,
    phase: 'ready',
    stageScore: 0,
    combo: 0,
    maxCombo: 0,
    pulses: MAX_PULSES,
    retries: run.retries + (countRetry ? 1 : 0),
    elapsed: 0,
    petalsCollected: 0,
    lastPetalAt: Number.NEGATIVE_INFINITY,
    failure: null,
  };
}

export function collectPetal(run: RunState, now: number): { run: RunState; award: number } {
  const combo = nextCombo(run.combo, now - run.lastPetalAt);
  const award = petalAward(combo);
  return {
    award,
    run: {
      ...run,
      combo,
      maxCombo: Math.max(run.maxCombo, combo),
      stageScore: run.stageScore + award,
      petalsCollected: run.petalsCollected + 1,
      lastPetalAt: now,
    },
  };
}

export function stageIsEligible(run: RunState, stage: StageDefinition): boolean {
  return run.petalsCollected >= stage.requiredPetals;
}

export function pauseRun(run: RunState): RunState {
  return run.phase === 'playing' ? { ...run, phase: 'paused' } : run;
}

export function resumeRun(run: RunState): RunState {
  return run.phase === 'paused' ? { ...run, phase: 'playing' } : run;
}
